import { Component, Input, OnInit, Renderer2 } from '@angular/core';
import { EquipmentFilters } from 'src/app/models/dashboard';
import { COLOR_LIST_EXTENDED, TimeFramesValues } from 'src/app/config/constants';
import moment from 'moment-timezone';
import _ from 'lodash';
import { SelectItem } from 'primeng/api';
import { BackendService } from 'src/app/services/backend/backend.service';
import { forkJoin } from 'rxjs';
import { TelemetryData } from 'src/app/models/telemetry-data';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import {CsvExportService} from "../../services/data/csv-export.service";
import * as Highcharts from 'highcharts';

const TIME_START_DATE = '00:00:00';
const TIME_END_DATE = '23:59:00';
@Component({
    standalone: false,
	selector: 'cc-historical-data',
	templateUrl: './historical-data.component.html',
	styleUrls: ['./historical-data.component.scss']
})
export class HistoricalDataComponent implements OnInit {
	@Input() pointsObj: {};

	historiesOptions: EquipmentFilters[] = [];
	selectedHistories: any[] = [];
	haveDisabledHistoriesOptions: boolean = false;
	isHistorySelectionActive: boolean = false;
	filterButtonScheme: string = 'discreetDark';
	noHistoriesMessage: string = '';
	historiesNoMaxLimit: number = null;

	start: Date = moment.utc().subtract(7, 'days').toDate();
	end: Date = moment.utc().toDate();
	maxDateValue: Date = moment.utc().toDate();
	minDateValue: Date = moment.utc().subtract(90, 'days').toDate();
	rangeDates: Date[];
	timeOptionList: SelectItem[] = [
		{ label: 'Select time', value: '' },
		{ label: 'Today', value: TimeFramesValues.TODAY },
		{ label: 'Yesterday', value: TimeFramesValues.YESTERDAY },
		{ label: 'Last 7 days', value: TimeFramesValues.LAST_7_DAYS },
		{ label: 'Last 30 days', value: TimeFramesValues.LAST_30_DAYS },
        { label: 'Last 180 days', value: TimeFramesValues.LAST_180_DAYS },
		{ label: 'Last 365 days', value: TimeFramesValues.LAST_365_DAYS }
	];
	selectedTime: SelectItem = { label: 'Last 7 days', value: 7 };

    selectedSite: {
        label: string,
        value: string,
        customerId: string,
        timezone: string
    } = JSON.parse(sessionStorage.getItem('selectedSite'));

	Highcharts: typeof Highcharts = Highcharts;
	chartOptions: Highcharts.Options;

	updateFlag: boolean = false;
	showChart: boolean = false;
	highchartsReady: boolean = false;

	constructor(private backendService: BackendService,
                private renderer: Renderer2,
                private breakpointObserver: BreakpointObserver,
                private csvExportService: CsvExportService,
    ) { }

	async ngOnInit(): Promise<void> {
		window.moment = moment;
		this.breakpointObserver
			.observe([Breakpoints.Small])
			.subscribe((state: BreakpointState) => {
				if (!state.matches) {
					this.noHistoriesMessage = 'Please select required histories from drop down menu<br />Maximum 10 histories can be viewed';
					this.historiesNoMaxLimit = 10;
				} else {
					this.noHistoriesMessage = 'Please select required histories from drop down menu<br />Maximum 5 histories can be viewed';
					this.historiesNoMaxLimit = 5;
				}
			});

		this.createHistoriesOptions();
		this.setDefaultTimeframe();
        await this.preloadHighcharts();
        this.initChartOptions(this.selectedSite.timezone);
	}

    async preloadHighcharts(): Promise<void> {
        await import('highcharts');
        await import('highcharts/esm/modules/stock');
        this.highchartsReady = true;
    }

    initChartOptions(timeZone: string) {
        this.chartOptions = {
            credits: {
                enabled: false,
            },
            title: {
                text: null,
            },
            subtitle: {
                text: null,
            },
            chart: {
                zooming: {
                    type: "x",
                },
                events: {
                    redraw: function (event) {
                        this.xAxis[0].setExtremes(null, null, false);
                    },
                },
            },
            legend: {
                verticalAlign: "top",
            },
            xAxis: {
                type: "datetime",
                dateTimeLabelFormats: {
                    hour: "%H:%M",
                    minute: "%H:%M",
                    second: "%H:%M:%S",
                    day: "%e %b",
                },
                labels: {
                    formatter: function () {
                        let localeTime = new Intl.DateTimeFormat("en-US", {
                            timeZone: timeZone,
                            hour: "2-digit",
                            minute: "2-digit",
                            hourCycle: "h23",
                            hour12: false,
                        }).format(Number(this.value));

                        const [hour, min] = localeTime.split(":");
                        if (Number(hour) === 0 && Number(min) === 0) {
                            return new Intl.DateTimeFormat("en-US", {
                                timeZone: timeZone,
                                month: "short",
                                day: "2-digit",
                                hourCycle: "h23",
                                hour12: false,
                            }).format(Number(this.value));
                        } else {
                            return localeTime;
                        }
                    },
                },
                zoomEnabled: true,
            },
            yAxis: [],
            tooltip: {
                shared: true,
                useHTML: true,
                xDateFormat: '%A, %b %e, %H:%M:%S.%L',
                pointFormatter: function () {
                    let s = '';
                    this.points?.forEach((point) => {
                        // Access maskValue
                        s += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y}</b>`;
                        if ((point as any).maskValue) {//Convert Point type to any type in order to use maskValue - a custom property
                            s += ` - <b>${(point as any).maskValue}</b>`;
                        }
                        s += `<br/>`;
                    });
                    return s;
                },
            },
            plotOptions: {
                line: {
                    marker: {
                        radius: 3,
                    },
                    lineWidth: 1,
                },
            },
            navigator: {
                enabled: true,
            },
            time: {
                timezone: this.selectedSite.timezone,
            },
        };
    }

	public createHistoriesOptions() {
		let rootEqClassLabel = '';
		let pointsArray = [];
		let currentObj = {}

		Object.keys(this.pointsObj).forEach((eqName) => {
			rootEqClassLabel = eqName;
			currentObj = this.pointsObj[eqName];

            Object.keys(currentObj).map((key, index) => {
                pointsArray.push(...currentObj[key].map(point => {
                    if (index === 0) {
                        return {
                            label: `${point.comment || point.sourceDescription || point.classLabel}`,
                            value: Object.assign({}, point, {chartLegend: `${eqName} - ${point.comment || point.sourceDescription || point.classLabel}`}),
                            pointUUID: point.pointUUID,
                            disabled: false
                        }
                    }
                    return {
                        label: `${key} - ${point.comment || point.sourceDescription || point.classLabel}`,
                        value: Object.assign({}, point, {chartLegend: `${eqName} - ${point.comment || point.sourceDescription || point.classLabel}`}),
                        pointUUID: point.pointUUID,
                        disabled: false
                    }
                }))
            });

			this.historiesOptions.push({
				label: rootEqClassLabel,
				value: rootEqClassLabel,
				items: pointsArray
			});
			pointsArray = [];
		});
	}

	public onIntervalSelected(event: any) {
		this.resetSelectedTime();
	}

	public onFilterByDate() {
		this.getFormattedTelemetryData();
	}

	public onIntervalOptionSelected() {
		this.setCalendarInterval();
	}

	public applyHistories(event: any, historiesDDRef: any) {
		if (!this.selectedHistories.length) {
			this.clearHistories(event, historiesDDRef);
			return;
		}
		this.getFormattedTelemetryData();
		historiesDDRef.close(event);
	}

	public clearHistories(event: any, historiesDDRef: any) {
		this.selectedHistories = [];
		this.showChart = false;
		this.setDefaultTimeframe();
		this.onHistoriesSelectionChange();
		historiesDDRef.close(event);
	}

	public onHistoriesSelectionChange() {
		if (this.selectedHistories.length === this.historiesNoMaxLimit) {
			this.historiesOptions.map(historyOption => {
				_.differenceBy(historyOption.items, this.selectedHistories, 'pointUUID').map(item => item.disabled = true);
				this.haveDisabledHistoriesOptions = true;
			})
		}
		if (this.selectedHistories.length < this.historiesNoMaxLimit && this.haveDisabledHistoriesOptions) {
			this.historiesOptions.map(historyOption => {
				historyOption.items.map(item => item.disabled = false);
			});
			this.haveDisabledHistoriesOptions = false;
		}
	}

	public applyHistoriesOnMobile() {
		if (!this.selectedHistories.length) {
			this.clearHistoriesOnMobile();
			return;
		}
		this.getFormattedTelemetryData();
		this.closeHistoriesSelectionOnMobile();
	}

	public clearHistoriesOnMobile() {
		this.selectedHistories = [];
		this.showChart = false;
		this.setDefaultTimeframe();
		this.onHistoriesSelectionChange();
		this.closeHistoriesSelectionOnMobile();
	}

	public showHistoriesSelectionMenuOnMobile() {
		this.renderer.addClass(document.body, 'noscroll');
		this.renderer.addClass(document.getElementById('app-wrapper'), 'noscroll');
		this.isHistorySelectionActive = true;
	}

	public closeHistoriesSelectionOnMobile() {
		this.renderer.removeClass(document.body, 'noscroll');
		this.renderer.removeClass(document.getElementById('app-wrapper'), 'noscroll');
		this.isHistorySelectionActive = false;
	}

    /**
     * @description Creates a Date object based on date string, and timezone using Moment.js
     * @param {string} dateInput - Date in 'YYYY-MM-DD' format
     * @param {string} timeInput - Time in 'HH:MM:SS' format (24-hour)
     * @param {string} timezoneInput - Timezone name (e.g., 'America/New_York')
     * @returns {Date} - JavaScript Date object with the inputted timezone
     */
    createDateWithMoment(dateInput: string, timezoneInput: string) {
        try {
          let dateTimeStr = `${dateInput}`;
          // Create moment object in the specified timezone
          let momentDate = moment.tz(dateTimeStr, "YYYY-MM-DD HH:mm:ss", timezoneInput);
          return momentDate;
        } catch (error) {
          console.error("Error creating date with Moment:", error.message);
          return null;
        }
    }

    /**
     * @description create a date string from a given date and a time string
     * @param date
     * @returns a string in YYYY-MM-DD HH:mm:ss format
     */
    createDateStrFromDate(date: Date, timeInput: string) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day} ${timeInput}`;
    }

	private getFormattedTelemetryData() {
        let end = "";
        let start = this.createDateWithMoment(
            this.createDateStrFromDate(this.rangeDates[0], TIME_START_DATE),
            this.selectedSite.timezone
        )
            .utc()
            .format();

        if (this.rangeDates[1]) {
            end = this.createDateWithMoment(
                this.createDateStrFromDate(this.rangeDates[1], TIME_END_DATE),
                this.selectedSite.timezone
            )
                .utc()
                .format();
        } else {
            end = this.createDateWithMoment(
                this.createDateStrFromDate(this.rangeDates[0], TIME_END_DATE),
                this.selectedSite.timezone
            )
                .utc()
                .format();
        }

        this.getTelemetryData(this.selectedHistories.map(point => point.pointUUID), start, end).subscribe((response => {
            this.chartOptions.series = this.formatTelemetryData(response);
            this.updateFlag = true;
            this.showChart = true;
        }));
    }
	private getTelemetryData(pointUUIDList: string[], start?: string, end?: string) {
		const startDate = start || moment.utc().subtract(7, 'days').format();
		const endDate = end || moment.utc().format();
		let requestsArray = pointUUIDList.map(pointUUID => {
			return this.backendService.getTelemetryData(pointUUID, startDate, endDate);
		});

		return forkJoin(requestsArray);
	}

	private formatTelemetryData(dataToFormat: TelemetryData[]) {
		let data: any[] = [];
		this.selectedHistories.forEach((point, index) => {
			if (dataToFormat[index] == null) { return; }
			dataToFormat[index].chartLegend = point.chartLegend;
		})

		let filterdByNull = dataToFormat.filter(telemetryData => telemetryData !== null);
		const groupedByUnit = _.groupBy(filterdByNull, (telemetryData) => telemetryData.units);
		let colorIndex = 0;

		Object.keys(groupedByUnit).map((unit, index) => {
			let unitType = unit === "null" || unit === "noUnits" ? '' : unit;
			// let color = this.getColorByIndex(index) || Highcharts.getOptions().colors[index];
			let color = '#000000';


			this.chartOptions.yAxis[index] =
			{
				title: {
					text: unitType,
					style: {
						color: color
					}
				},
				labels: {
					style: {
						color: color
					}
				},
				opposite: false
			};

			groupedByUnit[unit].forEach((telemetryData, i) => {
				if (telemetryData != null) {
					data.push({
						name: telemetryData.chartLegend,
						type: 'line',
						dashStyle: 'solid',
						yAxis: index,
						color: this.getColorByIndex(colorIndex++),
						data: telemetryData.samples.sort((a, b) => { return a.timestamp - b.timestamp }).map(entry => {
							return {
                                x: entry.timestamp,
                                y: entry.value,
                                maskValue: entry.maskValue
                            };
						})
					});
				};
			})
		});
		return data;
	}

	private setCalendarInterval() {
		let start = moment(TIME_START_DATE, 'HH:mm:ss').utc().subtract(this.selectedTime.value, 'days').toDate();
		let end = moment(TIME_END_DATE, 'HH:mm:ss').utc().toDate();

		if (this.selectedTime.value === TimeFramesValues.YESTERDAY) {
			end = moment(start).endOf("day").seconds(0).milliseconds(0).toDate();
		}

		this.rangeDates = [start, end];
	}

	private resetSelectedTime() {
		this.selectedTime = { label: 'Select time', value: '' };
	}

	private setDefaultTimeframe() {
		this.selectedTime = { label: 'Last 7 days', value: TimeFramesValues.LAST_7_DAYS };
		this.setCalendarInterval();
	}

	private getColorByIndex(index: number): string {
		return COLOR_LIST_EXTENDED[index];
    }

    exportToCsv() {
        this.csvExportService.exportToCsv(this.chartOptions, `${this.selectedSite.label} Data Points History ${moment(new Date()).format('YYYYMMDD HH:mm')}`);
    }
}
